import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Tag, Input, Select, Space, Typography, Tooltip, Table, Statistic, Segmented, message } from 'antd';
import { RobotOutlined, SearchOutlined, InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';
import {
  Yi, Ai360, Qwen, Claude, Wenxin, Doubao, Cloudflare, Cohere, DeepSeek, Gemini, Kling, Mistral, Moonshot,
  OpenAI, Spark, XAI, Zhipu, VertexAI, Ollama, Aws, Meta, Nvidia, Microsoft, Azure, Suno,
} from '@lobehub/icons';
import llmService from '../../services/llmService';

const { Text } = Typography;

// new-api vendor icon names ("Claude.Color") → the LobeHub components we bundle
const ICONS = { Yi, Ai360, Qwen, Claude, Wenxin, Doubao, Cloudflare, Cohere, DeepSeek, Gemini, Kling, Mistral, Moonshot,
  OpenAI, Spark, XAI, Zhipu, VertexAI, Ollama, Aws, Meta, Nvidia, Microsoft, Azure, Suno };

const VendorIcon = ({ icon, size = 18 }) => {
  const [base, variant] = String(icon || '').split('.');
  const C = ICONS[base.charAt(0).toUpperCase() + base.slice(1)];
  if (!C) return <RobotOutlined style={{ fontSize: size, color: '#999' }} />;
  const V = variant && C[variant] ? C[variant] : C;
  return <V size={size} />;
};

const usd = (n) => {
  if (n == null) return '-';
  if (n === 0) return '$0';
  return `$${n < 0.01 ? Number(n.toPrecision(2)) : Number(n.toFixed(4))}`;
};

/** Model list priced for the token group the customer picks (base price × group ratio). Data: /api/user/llm/pricing?format=catalog */
const Models = () => {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState({ models: [], vendors: [], groupRatio: {} });
  const [userGroups, setUserGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [vendor, setVendor] = useState('all');
  const [group, setGroup] = useState('default');
  const [billing, setBilling] = useState('all');

  useEffect(() => {
    setLoading(true);
    llmService.getModelCatalog()
      .then((r) => {
        if (r.success && Array.isArray(r.data?.models)) setCatalog(r.data);
        else message.error(r.error || t('Failed to retrieve model pricing'));
      })
      .catch(() => message.error(t('Failed to retrieve model pricing')))
      .finally(() => setLoading(false));
    llmService.getGroups().then((r) => r.success && r.data && setUserGroups(r.data)).catch(() => {});
  }, [t]);

  // groups the customer can put on a token (falls back to every priced group if the list isn't available)
  const groupOptions = useMemo(() => {
    const keys = Object.keys(userGroups).filter((k) => k !== 'auto');
    const list = keys.length ? keys : Object.keys(catalog.groupRatio || {});
    return list.map((k) => ({
      value: k,
      ratio: Number(catalog.groupRatio?.[k] ?? userGroups[k]?.ratio ?? 1),
      label: k === 'default' ? t('Default group') : k,
    }));
  }, [userGroups, catalog.groupRatio, t]);
  const ratio = groupOptions.find((g) => g.value === group)?.ratio ?? Number(catalog.groupRatio?.[group] ?? 1);

  const inGroup = useMemo(() => catalog.models.filter((m) => m.groups.includes(group)).map((m) => ({
    ...m,
    input: m.input_per_m == null ? null : m.input_per_m * ratio,
    output: m.output_per_m == null ? null : m.output_per_m * ratio,
    request: m.per_request == null ? null : m.per_request * ratio,
    free: m.per_request == null && !m.input_per_m && !m.output_per_m,
  })), [catalog.models, group, ratio]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return inGroup.filter((m) => (vendor === 'all' || m.vendor === vendor)
      && (billing === 'all' || (billing === 'free' ? m.free : billing === 'request' ? m.request != null : m.request == null && !m.free))
      && (!q || m.name.toLowerCase().includes(q) || m.description.toLowerCase().includes(q)));
  }, [inGroup, vendor, billing, search]);

  const vendorOptions = useMemo(() => {
    const count = {};
    inGroup.forEach((m) => { count[m.vendor] = (count[m.vendor] || 0) + 1; });
    return [{ value: 'all', label: t('All providers') }, ...catalog.vendors.filter((v) => count[v.name]).map((v) => ({
      value: v.name,
      label: <Space size={6}><VendorIcon icon={v.icon} size={14} />{t(v.name)}<Text type="secondary">{count[v.name]}</Text></Space>,
    }))];
  }, [catalog.vendors, inGroup, t]);
  const iconOf = useMemo(() => Object.fromEntries(catalog.vendors.map((v) => [v.name, v.icon])), [catalog.vendors]);

  const copy = (text) => navigator.clipboard.writeText(text).then(() => message.success(t('Copied')), () => {});

  const columns = [
    {
      title: t('Model'), dataIndex: 'name', key: 'name',
      render: (name, m) => (
        <Space size={6}>
          <VendorIcon icon={iconOf[m.vendor]} />
          <Text strong>{name}</Text>
          <CopyOutlined style={{ color: '#999', cursor: 'pointer' }} onClick={() => copy(name)} />
          {m.description && <Tooltip title={m.description}><InfoCircleOutlined style={{ color: '#999' }} /></Tooltip>}
        </Space>
      ),
    },
    { title: t('Provider'), dataIndex: 'vendor', key: 'vendor', width: 170, responsive: ['md'], render: (v) => t(v) },
    {
      title: t('Input / 1M tokens'), key: 'input', width: 150, align: 'right',
      sorter: (a, b) => (a.input ?? a.request ?? 0) - (b.input ?? b.request ?? 0),
      render: (_, m) => (m.free ? <Tag color="green">{t('Free')}</Tag>
        : m.request != null ? <Tooltip title={t('Pay per request')}>{usd(m.request)} / {t('request')}</Tooltip> : usd(m.input)),
    },
    {
      title: t('Output / 1M tokens'), key: 'output', width: 150, align: 'right',
      sorter: (a, b) => (a.output ?? 0) - (b.output ?? 0),
      render: (_, m) => (m.free || m.request != null ? '-' : usd(m.output)),
    },
    {
      title: t('Tag'), dataIndex: 'tags', key: 'tags', responsive: ['lg'],
      render: (tags) => String(tags || '').split(',').filter(Boolean).slice(0, 3).map((x) => <Tag key={x}>{x}</Tag>),
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={8}><Card size="small"><Statistic title={t('Available models')} value={inGroup.length} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title={t('Provider count')} value={vendorOptions.length - 1} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title={t('Free models')} value={inGroup.filter((m) => m.free).length} /></Card></Col>
      </Row>

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Input placeholder={t('Search model name or description')} prefix={<SearchOutlined />} value={search}
            onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} allowClear />
          <Select value={vendor} onChange={setVendor} style={{ width: 220 }} options={vendorOptions} showSearch
            optionFilterProp="value" popupMatchSelectWidth={false} />
          {groupOptions.length > 1 && (
            <Tooltip title={t('Prices for tokens in this group')}>
              <Select value={group} onChange={(g) => { setGroup(g); setVendor('all'); }} style={{ width: 200 }}
                options={groupOptions.map((g) => ({ value: g.value, label: <Space size={6}>{g.label}<Text type="secondary">×{g.ratio}</Text></Space> }))} />
            </Tooltip>
          )}
          <Segmented value={billing} onChange={setBilling} options={[
            { value: 'all', label: t('All') },
            { value: 'token', label: t('Per token') },
            { value: 'request', label: t('Per request') },
            { value: 'free', label: t('Free') },
          ]} />
        </Space>
        <Table rowKey="name" size="middle" loading={loading} dataSource={rows} columns={columns}
          pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200], showTotal: (n) => t('{{count}} models', { count: n }) }}
          scroll={{ x: 640 }} />
      </Card>
    </div>
  );
};

export default Models;
